<template>
  <div class="search-select" ref="wrapper">
    <!-- Trigger -->
    <button
      type="button"
      @click="toggle"
      :class="[
        'search-select-trigger',
        open ? 'is-open' : '',
        modelValue ? 'has-value' : '',
        compact ? 'is-compact' : ''
      ]"
    >
      <span v-if="icon" class="search-select-icon" v-html="icon" />
      <span class="search-select-label" :class="{ 'text-gray-400': !selectedLabel }">
        {{ selectedLabel || placeholder }}
      </span>
      <svg class="search-select-chevron" viewBox="0 0 16 16" fill="currentColor">
        <path d="M4.646 6.646a.5.5 0 01.708 0L8 9.293l2.646-2.647a.5.5 0 01.708.708l-3 3a.5.5 0 01-.708 0l-3-3a.5.5 0 010-.708z"/>
      </svg>
    </button>

    <!-- Dropdown -->
    <Transition name="dropdown">
      <div v-if="open" class="search-select-dropdown">
        <!-- Search input -->
        <div class="search-select-search">
          <svg class="search-select-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref="searchInput"
            v-model="query"
            type="text"
            :placeholder="searchPlaceholder"
            class="search-select-search-input"
            @input="onSearch"
          />
          <button v-if="query" @click="clearSearch" class="search-select-search-clear">
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Options list -->
        <div class="search-select-options">
          <div v-if="loading" class="search-select-empty">
            <span class="text-gray-400">Searching...</span>
          </div>
          <template v-else-if="filteredOptions.length">
            <button
              v-for="option in filteredOptions"
              :key="option[valueKey]"
              type="button"
              @click="select(option)"
              :class="[
                'search-select-option',
                modelValue === option[valueKey] ? 'is-selected' : ''
              ]"
            >
              <span class="search-select-option-label">{{ option[labelKey] }}</span>
              <svg v-if="modelValue === option[valueKey]" class="search-select-option-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </button>
          </template>
          <div v-else class="search-select-empty">
            <span class="text-gray-400">{{ query ? 'No results found' : 'No options available' }}</span>
          </div>
        </div>

        <!-- Clear selection -->
        <div v-if="modelValue && clearable" class="search-select-footer">
          <button type="button" @click="clear" class="search-select-clear-btn">
            Clear selection
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
export default {
  name: 'SearchSelect',

  props: {
    modelValue: { type: [String, Number], default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Select...' },
    searchPlaceholder: { type: String, default: 'Search...' },
    labelKey: { type: String, default: 'name' },
    valueKey: { type: String, default: '_id' },
    icon: { type: String, default: null },
    clearable: { type: Boolean, default: true },
    remote: { type: Boolean, default: false },
    compact: { type: Boolean, default: false }
  },

  emits: ['update:modelValue', 'search'],

  data() {
    return {
      open: false,
      query: '',
      loading: false,
      debounceTimer: null
    }
  },

  computed: {
    selectedLabel() {
      const selected = this.options.find(o => o[this.valueKey] === this.modelValue)
      return selected ? selected[this.labelKey] : ''
    },
    filteredOptions() {
      if (this.remote || !this.query) return this.options
      const q = this.query.toLowerCase()
      return this.options.filter(o => o[this.labelKey]?.toLowerCase().includes(q))
    }
  },

  methods: {
    toggle() {
      this.open ? this.close() : this.openDropdown()
    },

    openDropdown() {
      this.open = true
      this.$nextTick(() => {
        this.$refs.searchInput?.focus()
      })
      document.addEventListener('click', this.onClickOutside)
    },

    close() {
      this.open = false
      this.query = ''
      document.removeEventListener('click', this.onClickOutside)
    },

    onClickOutside(e) {
      if (!this.$refs.wrapper?.contains(e.target)) {
        this.close()
      }
    },

    select(option) {
      this.$emit('update:modelValue', option[this.valueKey])
      this.close()
    },

    clear() {
      this.$emit('update:modelValue', '')
      this.close()
    },

    clearSearch() {
      this.query = ''
      if (this.remote) this.$emit('search', '')
      this.$refs.searchInput?.focus()
    },

    onSearch() {
      if (!this.remote) return
      clearTimeout(this.debounceTimer)
      this.loading = true
      this.debounceTimer = setTimeout(() => {
        this.$emit('search', this.query)
      }, 300)
    },

    setLoading(val) {
      this.loading = val
    }
  },

  beforeUnmount() {
    document.removeEventListener('click', this.onClickOutside)
    clearTimeout(this.debounceTimer)
  }
}
</script>

<style>
.search-select {
  position: relative;
}

.search-select-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  height: 42px;
  padding: 0 36px 0 12px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #262626;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  text-align: left;
  position: relative;
}

.search-select-trigger.is-compact {
  height: 36px;
  font-size: 13px;
  border-radius: 8px;
  padding: 0 30px 0 10px;
}

.search-select-trigger.is-compact .search-select-chevron {
  right: 8px;
}

.search-select-trigger:focus,
.search-select-trigger.is-open {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-glow);
  outline: none;
}

.search-select-icon {
  display: flex;
  align-items: center;
  margin-right: 10px;
  color: #a3a3a3;
  flex-shrink: 0;
}

.search-select-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-select-chevron {
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
  color: #a3a3a3;
  transition: transform 0.2s;
}

.search-select-trigger.is-open .search-select-chevron {
  transform: rotate(180deg);
}

/* Dropdown */
.search-select-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  min-width: 100%;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 4px 10px -3px rgba(0, 0, 0, 0.04);
  z-index: 100;
  overflow: hidden;
}

/* Search */
.search-select-search {
  display: flex;
  align-items: center;
  padding: 4px 10px;
  border-bottom: 1px solid #f5f5f5;
  gap: 6px;
  min-height: 28px;
}

.search-select-search-icon {
  width: 14px;
  height: 14px;
  color: #a3a3a3;
  flex-shrink: 0;
}

.search-select-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #262626;
  background: transparent;
  height: 24px;
  line-height: 24px;
  padding: 0;
  box-shadow: none;
}

.search-select-search-input:focus,
.search-select-search-input:focus-visible {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
  /* Suppresses both browser default focus ring AND any global form
   * stylesheet that targets `input:focus` (e.g. Tailwind base, the
   * project's `.btn-primary:focus` rule, etc.) since the search box
   * already lives inside a popover with its own focused container. */
}

.search-select-search-input::placeholder {
  color: #d4d4d4;
}

.search-select-search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: none;
  color: #a3a3a3;
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;
}

.search-select-search-clear:hover {
  color: #525252;
  background: #f5f5f5;
}

/* Options */
.search-select-options {
  max-height: 240px;
  overflow-y: auto;
  padding: 4px;
}

.search-select-option {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 9px 12px;
  border: none;
  background: none;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #525252;
  cursor: pointer;
  border-radius: 8px;
  text-align: left;
  transition: background 0.1s, color 0.1s;
}

.search-select-option:hover {
  background: #f5f5f5;
  color: #262626;
}

.search-select-option.is-selected {
  color: var(--color-primary);
  font-weight: 500;
  background: var(--color-primary-light);
}

.search-select-option-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-select-option-check {
  width: 16px;
  height: 16px;
  color: var(--color-primary);
  flex-shrink: 0;
  margin-left: 8px;
}

.search-select-empty {
  padding: 20px 12px;
  text-align: center;
  font-size: 13px;
}

/* Footer */
.search-select-footer {
  border-top: 1px solid #f5f5f5;
  padding: 6px;
}

.search-select-clear-btn {
  display: block;
  width: 100%;
  padding: 7px 12px;
  border: none;
  background: none;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #a3a3a3;
  cursor: pointer;
  border-radius: 6px;
  text-align: center;
  transition: background 0.1s, color 0.1s;
}

.search-select-clear-btn:hover {
  background: #fef2f2;
  color: #ef4444;
}

/* Transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
